import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MedicineService } from 'src/core/services/medicine.service';
import { DosageType, DosageUnit } from 'src/interfaces/medicine';

@Component({
  selector: 'core-dosage-type-search-field',
  templateUrl: './dosage-type-search-field.component.html',
  styleUrls: ['./dosage-type-search-field.component.scss'],
  standalone: false
})
export class DosageTypeSearchFieldComponent implements OnInit, OnDestroy {

  @Input({ required: true }) control!: AbstractControl;
  @Input({ required: true }) dosageUnits!: DosageUnit[];
  @Input() label = 'Type';
  @Output() dosageTypeCreated = new EventEmitter<DosageType>();

  dosageTypes: DosageType[] = [];
  isSearching = false;

  private readonly destroy$ = new Subject<void>();
  private readonly searchLimit = 50;

  constructor(private _medicineService: MedicineService) { }

  ngOnInit(): void {
    this.loadDosageTypes('');

    const currentValue = this.control.value as DosageType | string | null;
    if (currentValue && typeof currentValue === 'object') {
      this.ensureSelectedInList(currentValue);
    }

    this.control.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: DosageType | string | null) => {
        if (typeof value === 'string') {
          this.loadDosageTypes(value);
        } else if (value && typeof value === 'object') {
          this.ensureSelectedInList(value);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  displayDosageType = (type: DosageType | string | null): string => {
    if (!type) {
      return '';
    }

    return typeof type === 'string' ? type : type.type;
  };

  onSearchInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const currentValue = this.control.value as DosageType | string | null;

    if (!currentValue || typeof currentValue === 'object') {
      this.control.setValue(inputValue, { emitEvent: true });
    }
  }

  async loadDosageTypes(search: string): Promise<void> {
    this.isSearching = true;

    try {
      this.dosageTypes = await this._medicineService.getDosageTypes(1, this.searchLimit, search);

      const currentValue = this.control.value as DosageType | string | null;
      if (currentValue && typeof currentValue === 'object') {
        this.ensureSelectedInList(currentValue);
      }
    } finally {
      this.isSearching = false;
    }
  }

  async createDosageType(event: Event): Promise<void> {
    event.stopPropagation();

    const newDosageType: DosageType = await this._medicineService
      .openDosageTypeCreateDialog(
        'New dosage type',
        'Create new dosage type',
        this.dosageUnits
      );

    if (!newDosageType) {
      return;
    }

    this.ensureSelectedInList(newDosageType);
    this.control.setValue(newDosageType);
    this.dosageTypeCreated.emit(newDosageType);
  }

  private ensureSelectedInList(dosageType: DosageType): void {
    if (!this.dosageTypes.some(type => type.id === dosageType.id)) {
      this.dosageTypes = [dosageType, ...this.dosageTypes];
    }
  }

}
